import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { ShieldX } from 'lucide-react';
import type { ReactNode } from 'react';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { SignOutOverlay, useSignOut } from '@/hooks/useSignOut';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { isHubAuthorized } from '@/lib/hub-authorization';

interface HubAuthorizedRouteProps {
  children: ReactNode;
}

/**
 * Top-level Hub authorization guard.
 * Blocks unauthorized users (e.g. customers) from entering the Hub shell.
 * Must wrap all Hub routes — authentication alone is not sufficient.
 * Uses multi-role JWT `roles[]` so organization-member + module role stacks work.
 */
export function HubAuthorizedRoute({ children }: HubAuthorizedRouteProps) {
  const { organizationId, isLoading: authLoading } = useAuth();
  const { roles, isLoading: permissionsLoading } = useUserPermissions();
  const { isSigningOut, handleSignOut } = useSignOut();

  // AppContent already returns early while auth is loading, but we keep this
  // guard so the route remains self-contained and testable in isolation.
  if (authLoading || permissionsLoading) {
    return <Loader text="Checking access..." fullScreen />;
  }

  if (!isHubAuthorized(roles, organizationId)) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <SignOutOverlay visible={isSigningOut} />
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="error">
                <ShieldX />
              </EmptyMedia>
              <EmptyTitle>Access denied</EmptyTitle>
              <EmptyDescription>
                This application is only available to authorized Hub users. If you believe this is an error, contact your administrator.
              </EmptyDescription>
              <Button variant="default" className="mt-4" onClick={handleSignOut}>
                Sign out
              </Button>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
